import { ErrorMessage, Field, FieldArray, Formik, Form as FormikForm } from "formik";
import { useEffect, useRef, useState } from "react";
import { Alert, Button, Col, Container, Row, Spinner, Table } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";
import { BOSS_MESSAGES, canEdit, canView, EMPLOYEE_MESSAGES, EVALUATION_SPECIFIC_VIEWS, QUARTERS, STATUSES } from "../../../pages/evaluacion_desempenio/utils/Constants";
import ReactExport from "react-export-excel";
import FileInput from "./FileInput";
import { useParams } from "react-router-dom/cjs/react-router-dom.min";
import { deleteEmployeeObjective, editEmployeeObjective, getEmployeeObjective, saveEmployeeObjective, setObjectivesModified } from "../../../redux/evaluacion_desempenio/EmployeeObjective/actions";

/**
 * ObjectiveEvaluationForm
 *
 * Componente encargado de:
 * - Mostrar y editar los objetivos de un empleado
 * - Soportar modos de edición restringidos según etapa y rol (empleado/jefe)
 * - Manejar subida/cambio de archivos adjuntos para cada objetivo
 * - Validar objetivos antes de enviarlos al backend
 *
 * Props:
 * - idUser: id del usuario actual (para decidir permisos de edición)
 * - handleHistory: callback para navegar al historial
 * - checkIfAvailableToBoss: función que verifica si el jefe puede actuar en ciertos periodos
 * - checkIfEmployeeCanEdit: función que verifica si el empleado puede editar en ciertos periodos
 * - setShowImportModal: función para abrir el modal de importación
 *
 */
const ObjectiveEvaluationForm = ({ idUser, evaluationRole, handleHistory, setShowImportModal, setObjectivesEvaluationReady }) => {
    // Referencia al Formik para marcar campos como touched desde handlers externos
    const formikRef = useRef();
    const dispatch = useDispatch();

    // Utilidades para exportar a Excel
    const ExcelFile = ReactExport.ExcelFile;
    const ExcelSheet = ReactExport.ExcelFile.ExcelSheet;
    const ExcelColumn = ReactExport.ExcelFile.ExcelColumn;

    // Estados locales para controlar fila en edición y si se está agregando una nueva
    const [editingIndex, setEditingIndex] = useState(null);
    const [isAdding, setIsAdding] = useState(false);

    // Parámetros de ruta necesarios para guardar/editar objetivos
    const { idEvaluacion, idConfiguracion, idEvaluacionGeneral } = useParams();

    // Selectores desde Redux: datos de objetivos y estado de carga/errores
    const {
        loading,
        saveLoading,
        employeeObjectivesLength,
        employeeObjectives,
        employeeObjectiveError,
        formError
    } = useSelector((store) => store.employeeObjective);

    // Selector del monitor de evaluación para mostrar mensajes y etapas
    const {
        loadingById: loadingEvaluationMonitor,
        evaluationMonitor,
        evaluationMonitorError
    } = useSelector((store) => store.evaluationMonitor);

    // Selector de pilares estratégicos para llenar selects
    const {
        loading: loadingPillars,
        strategicPillars,
        strategicPillarError,
    } = useSelector((store) => store.strategicPillar);

    const pillarOptions = strategicPillars?.map((pillar) => ({
        value: pillar.id,
        label: pillar.nombre
    })) || []


    // Determinar la etapa actual de la evaluación
    const evaluationEtapa = evaluationMonitor?.data_evaluacion_objetivos?.etapa;

    // Determinar si el usuario ya ha enviado la evaluación en el 3Q
    const evaluationDone = STATUSES.PENDIENTE_EVALUACION &&
        ((evaluationMonitor?.data_evaluacion_objetivos?.jefe_completo_evaluacion && idUser === evaluationMonitor?.evaluador) ||
            (evaluationMonitor?.data_evaluacion_objetivos?.empleado_completo_evaluacion && idUser === evaluationMonitor?.evaluado));


    //Determinar si el jefe puede ver la evaluación de competencias del empleado cuando este la haya enviado
    const canBossSeeEmployeeEvaluation = (evaluationMonitor?.data_evaluacion_objetivos?.empleado_completo_evaluacion && idUser === evaluationMonitor?.evaluador) || idUser === evaluationMonitor?.evaluado;

    // Schema de validación para cada objetivo usando Yup.
    const validationSchema = Yup.object().shape({
        objetivos: Yup.array()
            .of(
                Yup.object().shape({
                    pilar: Yup.string().required("Requerido"),
                    objetivo: Yup.string().required("Requerido"),
                    peso: Yup.number()
                        .typeError("Debe ser un número")
                        .min(0, "Mínimo 0")
                        .max(100, "Máximo 100")
                        .required("Requerido"),
                    indicador: Yup.string().required("Requerido"),
                    nota_empleado: Yup.number()
                        .transform((value, originalValue) => originalValue === "" ? null : value)
                        .nullable()
                        .min(0, "Mínimo 0")
                        .max(120, "Máximo 120"),
                    nota_jefe: Yup.number()
                        .transform((value, originalValue) => originalValue === "" ? null : value)
                        .nullable()
                        .min(0, "Mínimo 0")
                        .max(120, "Máximo 120")
                })
            )

    });


    useEffect(() => {
        //verificar si la evaluación de objetivos está lista para ser enviada según el rol y etapa
        if (employeeObjectives && STATUSES.PENDIENTE_EVALUACION === evaluationMonitor?.data_evaluacion_objetivos?.etapa) {
            if (idUser === evaluationMonitor?.evaluado) {
                //verificar si todos los objetivos tienen nota_empleado para considerar que la evaluación está lista
                const allRated = employeeObjectives.every(obj => obj.nota_empleado !== null && obj.nota_empleado !== undefined);
                setObjectivesEvaluationReady(allRated);
            } else if (idUser === evaluationMonitor?.evaluador) {
                //verificar si todos los objetivos tienen nota_jefe para considerar que la evaluación está lista
                const allRated = employeeObjectives.every(obj => obj.nota_jefe !== null && obj.nota_jefe !== undefined);
                setObjectivesEvaluationReady(allRated);
            }
        }
    }, [employeeObjectives, evaluationMonitor, idUser])

    /**
     * renderEvaluationMessage
     * - Decidir y retornar el Alert con el mensaje de estado para empleado/jefe según la etapa.
     * - La lógica busca en los objetos EMPLOYEE_MESSAGES y BOSS_MESSAGES por 'name' de etapa.
     */
    function renderEvaluationMessage() {
        const etapa = evaluationMonitor?.data_evaluacion_objetivos?.etapa;
        let msgObj;

        if (evaluationMonitor?.evaluado === idUser) {
            msgObj = Object.values(EMPLOYEE_MESSAGES).find(m => m.name === etapa);
        } else if (evaluationMonitor?.evaluador === idUser && typeof BOSS_MESSAGES !== "undefined") {
            msgObj = Object.values(BOSS_MESSAGES).find(m => m.name === etapa);
        }

        return msgObj ? (
            <Alert variant={msgObj.color} className="mt-3">
                {msgObj.message}
            </Alert>
        ) : null
    }


    /**
     * onSaveObjective
     * - Valida el conjunto de objetivos contra el schema (para marcar errores correctamente).
     * - Construye FormData para enviar al backend (incluye archivo si es un File).
     * - Diferencia entre crear (isAdding) y editar (editEmployeeObjective).
     * - Si hay errores de validación, marca los campos correspondientes como 'touched' en Formik.
     */
    const onSaveObjective = async (values, index) => {
        // Validar el objetivo individual usando el schema
        try {
            // Validación completa para poder marcar errores por campo
            await validationSchema.validate({ objetivos: values.objetivos }, { abortEarly: false });

            let formData = new FormData();
            formData.append("empleado_evaluacion", Number(idEvaluacion));
            formData.append("nombre", values.objetivos[index].objetivo);
            formData.append("pilar_estrategico", Number(values.objetivos[index].pilar));
            formData.append("peso", values.objetivos[index].peso);
            formData.append("indicador", values.objetivos[index].indicador);
            formData.append("configuracion", Number(idConfiguracion));

            // Solo adjuntar File si es un objeto File, no si viene como string (ruta)
            if (values.objetivos[index].archivo && typeof values.objetivos[index].archivo !== "string") {
                formData.append("archivo", values.objetivos[index].archivo);
            }
            if (values.objetivos[index].comentario) formData.append("comentario", values.objetivos[index].comentario);
            if (values.objetivos[index].nota_empleado) formData.append("nota_empleado", Number(values.objetivos[index].nota_empleado));
            if (values.objetivos[index].nota_jefe) formData.append("nota_jefe", Number(values.objetivos[index].nota_jefe));

            // Dispatch a las actions correspondientes
            if (isAdding) {
                await dispatch(saveEmployeeObjective(formData, setEditingIndex, setIsAdding))
            } else {
                const objetivoId = values.objetivos[index].id;
                if (objetivoId) {
                    await dispatch(editEmployeeObjective(formData, objetivoId, index, setEditingIndex))
                }
            }
            await dispatch(setObjectivesModified(true));
            // Refrescar lista de objetivos tras guardar/editar
            await dispatch(getEmployeeObjective({
                empleado_evaluacion: idEvaluacion
            }))
        } catch (err) {
            // Si hay errores de validación, marcarlos como touched para que Formik muestre mensajes
            if (formikRef.current) {
                const touched = { objetivos: [] };
                if (err.inner) {
                    err.inner.forEach(e => {
                        if (e.path.startsWith("objetivos")) {
                            const match = e.path.match(/objetivos\[(\d+)\]\.(\w+)/);
                            if (match) {
                                const idx = parseInt(match[1]);
                                const field = match[2];
                                touched.objetivos[idx] = touched.objetivos[idx] || {};
                                touched.objetivos[idx][field] = true;
                            }
                        }
                    });
                }
                formikRef.current.setTouched(touched, true);
            }
        }
    };

    /**
     * onCancel
     * - Si se estaba agregando una fila nueva y se cancela, eliminar la fila incompleta.
     * - Resetear estado de edición.
     */
    const onCancel = () => {
        //si se agregó una nueva fila pero no se completó entonces al cancelar, eliminarla
        //si se estaba editando pero si quiso cancelar, dejar la fila
        if (isAdding && formikRef.current) {
            formikRef.current.setFieldValue("objetivos", formikRef.current.values.objetivos.filter((_, i) => i !== formikRef.current.values.objetivos.length - 1));
        }
        setEditingIndex(null);
        setIsAdding(false);

    }

    /**
     * onDelete
     * - Dispara la acción que elimina el objetivo en backend y actualiza estado local.
     */
    const onDelete = (index) => {
        const objetivoId = employeeObjectives[index]?.id;
        if (objetivoId) {
            dispatch(deleteEmployeeObjective(objetivoId, index))
        }
    }

    return (
        <>
            <div style={{ position: 'relative' }}>
                {/* Loading overlay */}
                {(loading || saveLoading || loadingEvaluationMonitor) && (
                    <div 
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: 'rgba(255, 255, 255, 0.7)',
                            zIndex: 1000,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minHeight: '200px'
                        }}
                    >
                        <div className="text-center">
                            <Spinner animation="border" size="sm" className="mr-2" />
                            <span>Cargando...</span>
                        </div>
                    </div>
                )}

                <Formik
                    innerRef={formikRef}
                    initialValues={{
                        // Mapear los objetivos del store a la forma esperada por Formik
                        objetivos: employeeObjectives?.length > 0 ? employeeObjectives
                            .slice()
                            .sort((a, b) => a.id - b.id)
                            .map(obj => ({
                                pilar: obj.pilar_estrategico,
                                objetivo: obj.nombre,
                                peso: parseFloat(obj.peso),
                                indicador: obj.indicador,
                                id: obj.id,
                                nota_empleado: obj.nota_empleado,
                                nota_jefe: obj.nota_jefe,
                                metrica_nombre_data: obj.metrica_nombre_data,
                                comentario: obj.comentario,
                                archivo: obj.archivo
                            })) : []
                    }}
                    validationSchema={validationSchema}
                    enableReinitialize
                >
                    {({ values, errors, setFieldValue }) => {
                        // Calcular suma de pesos para validación visual
                        const pesoTotal = values.objetivos.reduce(
                            (acc, obj) => acc + (parseFloat(obj.peso) || 0),
                            0
                        );
                        const showErrorAlert = employeeObjectiveError || evaluationMonitorError || strategicPillarError || formError;
                        const isLoading = loading || saveLoading || loadingEvaluationMonitor;

                        return (
                            <FormikForm className="my-5">
                                <FieldArray name="objetivos">
                                    {({ push, remove }) => (
                                        <>
                                            {/* Alert: suma de pesos debe ser 100 */}
                                            <Alert variant="danger" className="mt-3" show={pesoTotal !== 100}>
                                                La suma de los pesos debe ser igual a 100 para enviar el formulario a revisión. Actualmente es {pesoTotal}.
                                            </Alert>

                                            {/* Alert: errores generales provenientes del backend o store */}
                                            <Alert variant="danger" className="mt-3" show={showErrorAlert}>
                                                {showErrorAlert}
                                            </Alert>

                                            {/* Mensaje contextual según etapa y rol */}
                                            {renderEvaluationMessage()}

                                            {/* Mensajes específicos de comentarios del jefe según etapa */}
                                            {
                                                evaluationMonitor?.data_evaluacion_objetivos?.comentario_revision_jefe && evaluationMonitor?.data_evaluacion_objetivos?.etapa === STATUSES.JEFE_NO_ACEPTA_OBJETIVOS && (
                                                    <div className="p-3 mb-3 border border-danger rounded">
                                                        <i className="material-icons text-danger">chat</i> {evaluationMonitor.data_evaluacion_objetivos.comentario_revision_jefe}
                                                    </div>
                                                )
                                            }
                                            {
                                                evaluationMonitor?.data_evaluacion_objetivos?.comentario_mitad_anio && evaluationMonitor?.data_evaluacion_objetivos?.etapa === STATUSES.REVISION_EN_PROCESO && (
                                                    <div className="p-3 mb-3 border border-danger rounded">
                                                        <i className="material-icons text-danger">chat</i> {evaluationMonitor.data_evaluacion_objetivos.comentario_mitad_anio}
                                                    </div>
                                                )
                                            }

                                            <div className="d-flex justify-content-between align-items-center">
                                                {
                                                    // Botón historial sólo si hay objetivos
                                                    employeeObjectivesLength > 0 && (
                                                        <Button className="rounded-pill" onClick={handleHistory}>Historial</Button>

                                                    )
                                                }
                                                <div className="d-flex justify-content-end flex-row my-3">
                                                    {
                                                        // Importar disponible solo si el empleado puede editar en first_q/second_q
                                                        canEdit(evaluationRole, [QUARTERS.FIRST, QUARTERS.SECOND], evaluationEtapa, null, EVALUATION_SPECIFIC_VIEWS.EMPLOYEE_OBJECTIVES) ? (
                                                            <Button
                                                                className="rounded-pill mr-2"
                                                                variant="outline-primary"
                                                                onClick={() => setShowImportModal(true)}
                                                            >
                                                                <i className="material-icons">upload</i> Importar
                                                            </Button>) : null
                                                    }

                                                    {/* Exportar objetivos a Excel */}
                                                    <ExcelFile
                                                        element={
                                                            <Button className="rounded-pill" variant="outline-primary" disabled={values.objetivos.length === 0}>
                                                                <i className="material-icons">download</i> Exportar
                                                            </Button>
                                                        }
                                                        filename="Objetivos"
                                                    >
                                                        <ExcelSheet data={employeeObjectives} name="Objetivos">
                                                            <ExcelColumn label="pilar estratégico" value={col => col.pilar_estrategico_data ? col.pilar_estrategico_data.nombre : ""} />
                                                            <ExcelColumn label="objetivo" value="nombre" />
                                                            <ExcelColumn label="peso" value="peso" />
                                                            <ExcelColumn label="indicador" value="indicador" />
                                                        </ExcelSheet>
                                                    </ExcelFile>
                                                </div>
                                            </div>

                                            {/* Tabla de objetivos: cada fila representa un objetivo que puede editarse según permisos y etapa */}
                                            <Table bordered hover responsive className={isLoading ? 'opacity-50' : ''}>
                                                <thead>
                                                    <tr>
                                                        <th style={{ minWidth: "200px" }}>Pilar Estratégico</th>
                                                        <th style={{ minWidth: '200px' }}>Objetivo</th>
                                                        <th style={{ minWidth: '100px' }}>Peso</th>
                                                        <th style={{ minWidth: '200px' }}>Indicador</th>
                                                        {
                                                            canView(evaluationRole, ['third_q'], evaluationEtapa, { condition: canBossSeeEmployeeEvaluation, quarter: QUARTERS.THIRD } ) ? <th style={{ minWidth: '100px' }}>Autoevaluación</th> : null
                                                        }
                                                        {
                                                            canView(evaluationRole, [QUARTERS.THIRD], evaluationEtapa, null, EVALUATION_SPECIFIC_VIEWS.BOSS_EVALUATION_COLUMNS) ?
                                                                <>
                                                                    <th style={{ minWidth: '200px' }}>Comentario</th>
                                                                    <th style={{ minWidth: '100px' }}>Evaluación Jefe</th>
                                                                    <th style={{ minWidth: '100px' }}>Métrica</th>
                                                                </> : null
                                                        }
                                                        {
                                                            canView(evaluationRole, [QUARTERS.THIRD], evaluationEtapa) ? <th>Archivo</th> : null
                                                        }
                                                        {
                                                            canEdit(evaluationRole, [QUARTERS.FIRST, QUARTERS.SECOND, QUARTERS.THIRD], evaluationEtapa, { condition: !evaluationDone, quarter: QUARTERS.THIRD }, EVALUATION_SPECIFIC_VIEWS.EMPLOYEE_OBJECTIVES) ? <th>Acciones</th> : null
                                                        }

                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {values.objetivos.map((_, index) => {
                                                        const isEditing = editingIndex === index;
                                                        return (
                                                            <tr key={index}>
                                                                <td className="bg-light">
                                                                    <Field
                                                                        as="select"
                                                                        name={`objetivos[${index}].pilar`}
                                                                        className="form-control"
                                                                        disabled={isLoading || !isEditing || canEdit(evaluationRole, [QUARTERS.FIRST, QUARTERS.SECOND], evaluationEtapa, null, EVALUATION_SPECIFIC_VIEWS.EMPLOYEE_OBJECTIVES) === false}
                                                                    >
                                                                        <option value="">Seleccione...</option>
                                                                        {pillarOptions.map((p) => (
                                                                            <option key={p.value} value={p.value}>
                                                                                {p.label}
                                                                            </option>
                                                                        ))}
                                                                    </Field>
                                                                    <ErrorMessage
                                                                        name={`objetivos[${index}].pilar`}
                                                                        component="div"
                                                                        className="text-danger small"
                                                                    />
                                                                </td>
                                                                <td>
                                                                    <Field
                                                                        as="textarea"
                                                                        name={`objetivos[${index}].objetivo`}
                                                                        className="form-control"
                                                                        disabled={isLoading || !isEditing || canEdit(evaluationRole, [QUARTERS.FIRST, QUARTERS.SECOND], evaluationEtapa, null, EVALUATION_SPECIFIC_VIEWS.EMPLOYEE_OBJECTIVES) === false}
                                                                    />
                                                                    <ErrorMessage
                                                                        name={`objetivos[${index}].objetivo`}
                                                                        component="div"
                                                                        className="text-danger small"
                                                                    />
                                                                </td>
                                                                <td>
                                                                    <Field
                                                                        type="number"
                                                                        name={`objetivos[${index}].peso`}
                                                                        className="form-control"
                                                                        disabled={isLoading || !isEditing || canEdit(evaluationRole, [QUARTERS.FIRST, QUARTERS.SECOND], evaluationEtapa, null, EVALUATION_SPECIFIC_VIEWS.EMPLOYEE_OBJECTIVES) === false}
                                                                    />
                                                                    <ErrorMessage
                                                                        name={`objetivos[${index}].peso`}
                                                                        component="div"
                                                                        className="text-danger small"
                                                                    />
                                                                </td>
                                                                <td>
                                                                    <Field
                                                                        as="textarea"
                                                                        name={`objetivos[${index}].indicador`}
                                                                        className="form-control"
                                                                        disabled={isLoading || !isEditing || canEdit(evaluationRole, [QUARTERS.FIRST, QUARTERS.SECOND], evaluationEtapa, null, EVALUATION_SPECIFIC_VIEWS.EMPLOYEE_OBJECTIVES) === false}
                                                                    />
                                                                    <ErrorMessage
                                                                        name={`objetivos[${index}].indicador`}
                                                                        component="div"
                                                                        className="text-danger small"
                                                                    />
                                                                </td>

                                                                {/* Columnas de evaluación del tercer Q (autoevaluación / jefe) */}
                                                                {
                                                                    canView(evaluationRole, ['third_q'], evaluationEtapa, { condition: canBossSeeEmployeeEvaluation, quarter: QUARTERS.THIRD } ) && (
                                                                        <td>
                                                                            <Field
                                                                                type="number"
                                                                                name={`objetivos[${index}].nota_empleado`}
                                                                                className="form-control"
                                                                                disabled={isLoading || !isEditing || canEdit(evaluationRole, [QUARTERS.THIRD], evaluationEtapa, { condition: !evaluationDone, quarter: QUARTERS.THIRD }, EVALUATION_SPECIFIC_VIEWS.EMPLOYEE_EVALUATION_COLUMN) === false}
                                                                            />
                                                                            <ErrorMessage
                                                                                name={`objetivos[${index}].nota_empleado`}
                                                                                component="div"
                                                                                className="text-danger small"
                                                                            />
                                                                        </td>

                                                                    )
                                                                }
                                                                {
                                                                    //si es el periodo de llenado de la evaluación se muestran las columnas
                                                                    canView(evaluationRole, [QUARTERS.THIRD], evaluationEtapa, null, EVALUATION_SPECIFIC_VIEWS.BOSS_EVALUATION_COLUMNS) && (
                                                                        <>
                                                                            <td>
                                                                                <Field
                                                                                    as="textarea"
                                                                                    name={`objetivos[${index}].comentario`}
                                                                                    className="form-control"
                                                                                    disabled={isLoading || !isEditing || canEdit(evaluationRole, [QUARTERS.THIRD], evaluationEtapa, { condition: !evaluationDone, quarter: QUARTERS.THIRD }) === false}
                                                                                />
                                                                                <ErrorMessage
                                                                                    name={`objetivos[${index}].comentario`}
                                                                                    component="div"
                                                                                    className="text-danger small"
                                                                                />
                                                                            </td>
                                                                            <td>
                                                                                <Field
                                                                                    type="number"
                                                                                    name={`objetivos[${index}].nota_jefe`}
                                                                                    className="form-control"
                                                                                    disabled={isLoading || !isEditing || canEdit(evaluationRole, [QUARTERS.THIRD], evaluationEtapa, { condition: !evaluationDone, quarter: QUARTERS.THIRD }) === false}
                                                                                />
                                                                                <ErrorMessage
                                                                                    name={`objetivos[${index}].nota_jefe`}
                                                                                    component="div"
                                                                                    className="text-danger small"
                                                                                />
                                                                            </td>
                                                                            <td>
                                                                                {values.objetivos[index]?.metrica_nombre_data || "-"}
                                                                            </td>

                                                                        </>
                                                                    )
                                                                }

                                                                {/* Input para archivo: usa el componente FileInput para mantener JSX limpio */}
                                                                {canView(evaluationRole, [QUARTERS.THIRD], evaluationEtapa) && (
                                                                    <td>
                                                                        <FileInput
                                                                            value={values.objetivos[index]?.archivo}
                                                                            onChange={e => {
                                                                                const file = e.target.files[0];
                                                                                if (file) {
                                                                                    setFieldValue(`objetivos[${index}].archivo`, file);
                                                                                }
                                                                            }}
                                                                            isEditing={isEditing && !isLoading}
                                                                        />
                                                                    </td>
                                                                )}

                                                                {/* Acciones por fila: editar / guardar / cancelar / eliminar */}
                                                                {
                                                                    (canEdit(evaluationRole, [QUARTERS.FIRST, QUARTERS.SECOND, QUARTERS.THIRD], evaluationEtapa, { condition: !evaluationDone, quarter: QUARTERS.THIRD }, EVALUATION_SPECIFIC_VIEWS.EMPLOYEE_OBJECTIVES)) ?
                                                                        <td>
                                                                            {isEditing ? (
                                                                                <>
                                                                                    <Button
                                                                                        variant="success"
                                                                                        size="sm"
                                                                                        disabled={isLoading}
                                                                                        onClick={() => onSaveObjective(values, index, setFieldValue)}
                                                                                    >

                                                                                        <i className="material-icons">save</i>
                                                                                    </Button>
                                                                                    <Button
                                                                                        variant="secondary"
                                                                                        size="sm"
                                                                                        className="ml-2"
                                                                                        disabled={isLoading}
                                                                                        onClick={() => onCancel()}
                                                                                    >
                                                                                        <i className="material-icons">cancel</i>
                                                                                    </Button>
                                                                                </>
                                                                            ) : (
                                                                                <>
                                                                                    <Button
                                                                                        variant="warning"
                                                                                        size="sm"
                                                                                        disabled={isLoading}
                                                                                        onClick={() => setEditingIndex(index)}
                                                                                    >
                                                                                        <i className="material-icons">edit</i>
                                                                                    </Button>
                                                                                    {
                                                                                        // Eliminar solo disponible para el empleado si está en etapa de edición
                                                                                        canEdit(evaluationRole, [QUARTERS.FIRST, QUARTERS.SECOND], evaluationEtapa) && (
                                                                                            <Button
                                                                                                variant="danger"
                                                                                                size="sm"
                                                                                                className="ml-2"
                                                                                                disabled={isLoading}
                                                                                                onClick={() => onDelete(index)}
                                                                                            >

                                                                                                <i className="material-icons">delete</i>
                                                                                            </Button>
                                                                                        )
                                                                                    }

                                                                                </>
                                                                            )}
                                                                        </td> : null
                                                                }

                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </Table>

                                            {/* Botón para agregar un nuevo objetivo (crea una fila editable al final) */}
                                            {
                                                canEdit(evaluationRole, [QUARTERS.FIRST, QUARTERS.SECOND], evaluationEtapa, null, EVALUATION_SPECIFIC_VIEWS.EMPLOYEE_OBJECTIVES) ? (
                                                    <div className="d-flex justify-content-between align-items-center mt-3">
                                                        <Button
                                                            variant="success"
                                                            onClick={() => {
                                                                push({ pilar: "", objetivo: "", peso: "", indicador: "" });
                                                                setEditingIndex(values.objetivos.length);
                                                                setIsAdding(true);
                                                            }}
                                                            disabled={isLoading || editingIndex !== null}
                                                        >
                                                            <i className="material-icons">add</i> Agregar Objetivo
                                                        </Button>
                                                    </div>
                                                ) : null
                                            }
                                        </>
                                    )}
                                </FieldArray>
                            </FormikForm>
                        )
                    }}
                </Formik>
            </div>

            {canView(evaluationRole, [QUARTERS.THIRD], evaluationEtapa, { condition: canBossSeeEmployeeEvaluation, quarter: QUARTERS.THIRD } ) && employeeObjectives?.length > 0 &&
                (<div className="my-3">
                    <Container fluid>
                        <Row>
                            <Col xs={12} md={6} className="mb-3">
                                <Row>
                                    <Col className="text-white bg-dark py-3">
                                        Resultado Autoevaluación
                                    </Col>
                                    <Col className="text-right py-3 bg-light">
                                        {employeeObjectives?.[0]?.empleado_evaluacion_data?.nota_empleado_final?.metrica_total || "-"}
                                    </Col>
                                </Row>
                            </Col>
                            {canView(evaluationRole, [QUARTERS.THIRD], evaluationEtapa, null, EVALUATION_SPECIFIC_VIEWS.BOSS_EVALUATION_RESULTS) &&
                                (<Col xs={12} md={6} className="mb-3">
                                    <Row>
                                        <Col className="text-white bg-dark py-3">
                                            Resultado Evaluación Jefe
                                        </Col>
                                        <Col className="text-right py-3 bg-light">
                                            {employeeObjectives?.[0]?.empleado_evaluacion_data?.nota_jefe_final?.metrica_total || "-"}
                                        </Col>
                                    </Row>
                                </Col>)}
                        </Row>
                    </Container>
                </div>)}
        </>



    )
}

export default ObjectiveEvaluationForm;